import { Injectable } from '@angular/core';
import {
  capitalizeFirstLetter,
  joinNotEmpty,
  replaceIllegalCharacters,
} from 'app/utils/string.utils';

import { ColumnFilterType } from './models/column-filter-type.enum';
import { ComparisonType, OdataOperator } from './models/comparison-type.enum';
import { FilterModel } from './models/filter.model';
import { DataRequest } from './models/query-builder-request.model';

const and = '&';

@Injectable({ providedIn: 'root' })
export class QueryBuilderService {
  private requestFieldNames: any = {};
  /* ODATA */

  buildOdataQuery(
    data: DataRequest,
    useInlineCount: boolean,
    externalFilter?: string
  ): string {
    this.requestFieldNames = data.requestFieldNames || {};
    return [
      this.getPaginationPart(data),
      this.getFilterPart(data, externalFilter),
      this.getSortingPart(data),
      useInlineCount ? '$inlinecount=allpages' : '',
    ]
      .filter((t) => !!t)
      .join(and);
  }

  /* Main parts of query building */

  private getPaginationPart(data: DataRequest): string {
    const pagination =
      data.skip > 0 && data.take > 0
        ? '$skip=' + data.skip + and + '$top=' + data.take
        : data.take > 0
        ? '$top=' + data.take
        : '';
    return pagination;
  }

  private getSortingPart(data: DataRequest, defaultOrderBy?: string): string {
    const sortModel: {
      field: string;
      direction: string;
    }[] = (data && data.orderBy) || [];

    return sortModel.length > 0
      ? '$orderby=' +
          sortModel
            .map((t) => `${this.modifyFieldName(t.field)} ${t.direction}`)
            .join(',')
      : defaultOrderBy
      ? `$orderby=${defaultOrderBy}`
      : '';
  }

  private getFilterPart(data: DataRequest, externalFilter: string): string {
    const andFilters = [];
    const orFilters = [];
    data.filters.forEach((filterItem) => {
      switch (OdataOperator[filterItem.type]) {
        case OdataOperator.Contains:
        case OdataOperator.NotContains:
          filterItem.isOrFilter
            ? orFilters.push(this.constructTextFilter(filterItem))
            : andFilters.push(this.constructTextFilter(filterItem));
          break;
        case OdataOperator.StartsWith:
        case OdataOperator.EndsWith:
          filterItem.isOrFilter
            ? orFilters.push(this.constructTextFilter(filterItem, true))
            : andFilters.push(this.constructTextFilter(filterItem, true));
          break;
        case OdataOperator.InRange:
          filterItem.isOrFilter
            ? orFilters.push(this.constructInRangeFilter(filterItem))
            : andFilters.push(this.constructInRangeFilter(filterItem));
          break;
        default:
          filterItem.isOrFilter
            ? orFilters.push(this.constructDefaultFilter(filterItem))
            : andFilters.push(this.constructDefaultFilter(filterItem));
          break;
      }
    });

    const andFilterPart = andFilters.join(' and ');
    let orFilterPart = orFilters.join(' or ');
    if (orFilterPart) {
      orFilterPart = `(${orFilterPart})`;
    }
    if (externalFilter) {
      externalFilter = `(${externalFilter})`;
    }
    let filterQuery = joinNotEmpty(
      [andFilterPart, orFilterPart, externalFilter],
      ' and '
    );

    if (filterQuery) {
      filterQuery = `$filter=${filterQuery}`;
    }

    return filterQuery;
  }

  /* Specific constructors by operators and data types */

  private constructTextFilter(
    filterItem: FilterModel,
    fieldFirst?: boolean
  ): string {
    const operator = OdataOperator[filterItem.type];
    const fieldName = this.modifyFieldName(filterItem.field);
    const filterValue = encodeURIComponent(replaceIllegalCharacters(filterItem.filter));
    return fieldFirst
      ? `${operator}(${fieldName},${filterValue})`
      : `${operator}(${filterValue},${fieldName})`;
  }

  private constructDefaultFilter(filterItem: FilterModel): string {
    if (!filterItem.filter && filterItem.filter !== 0) filterItem.filter = '';
    switch (filterItem.filterType) {
      case ColumnFilterType.Text:
        return `${this.modifyFieldName(filterItem.field)}${
          OdataOperator[filterItem.type]
        }${replaceIllegalCharacters(filterItem.filter)}`;
      case ColumnFilterType.Date:
        return filterItem.type === ComparisonType.Equals ||
          filterItem.type === ComparisonType.NotEqual
          ? this.constructDayFilter(filterItem)
          : `${this.modifyFieldName(filterItem.field)}${
              OdataOperator[filterItem.type] !== ComparisonType.Blank &&
              OdataOperator[filterItem.type] !== ComparisonType.NotBlank
                ? OdataOperator[filterItem.type]
                : ''
            }${this.convertToOdataDateFormat(filterItem.dateFrom)}`;
      case ColumnFilterType.Set:
        return this.constructSetFilter(filterItem);
      case ColumnFilterType.Boolean:
        return `${this.modifyFieldName(filterItem.field)}${
          OdataOperator[filterItem.type]
        }${this.convertToBoolString(filterItem.filter)}`;
      case ColumnFilterType.Custom:
        return `${filterItem.filter}`;
      default:
        return `${this.modifyFieldName(filterItem.field)}${
          OdataOperator[filterItem.type]
        }${filterItem.filter}`;
    }
  }

  private constructSetFilter(filterItem: FilterModel): string {
    if (!filterItem.values.find((v) => v === 'true' || v === 'false')) {
      filterItem.values = filterItem.values.map((v) =>
        ('' + v).match(/^-?\d+$/) ? +v : `'${v}'`
      );
    }
    const filterString =
      filterItem.values.length > 0
        ? `${this.modifyFieldName(
            filterItem.field
          )} in (${filterItem.values.join(',')})`
        : '';
    return filterString;
  }

  constructInRangeFilter(filterItem: FilterModel): string {
    const fieldName = this.modifyFieldName(filterItem.field);
    if (filterItem.filterType === ColumnFilterType.Date) {
      const dateFrom = this.convertToOdataDateFormat(filterItem.dateFrom);
      const dateTo = this.convertToOdataDateFormat(filterItem.dateTo);
      return `(${fieldName} ge ${dateFrom} and ${fieldName} le ${dateTo})`;
    }
    return `(${fieldName} ge ${filterItem.filter} and ${fieldName} le ${filterItem.filterTo} )`;
  }

  /* Helpers */

  convertToOdataDateFormat(
    dateString: string | Date,
    dateModifier?: number
  ): string {
    if (dateString) {
      let date = new Date(dateString);
      date = dateModifier ? new Date(+date + dateModifier) : date;
      return `datetime'${date.toISOString()}'`;
    }
    return '';
  }

  private convertToBoolString(value: string | number): string {
    if (value) {
      const lowValue = value.toString().toLowerCase();
      if (lowValue === 'true' || lowValue === '1') {
        return 'true';
      }
    }
    return 'false';
  }

  private constructDayFilter(filterItem: FilterModel): string {
    const dayInMs = 86399000;
    const start = this.convertToOdataDateFormat(filterItem.dateFrom);
    const end = this.convertToOdataDateFormat(filterItem.dateFrom, dayInMs);
    const dayIntervalQuery = `${this.modifyFieldName(
      filterItem.field
    )} ge ${start} and ${this.modifyFieldName(filterItem.field)} le ${end}`;

    return filterItem.type === ComparisonType.NotEqual
      ? `not(${dayIntervalQuery})`
      : dayIntervalQuery;
  }

  private modifyFieldName(field: string): string {
    return this.requestFieldNames[field]
      ? this.requestFieldNames[field]
      : capitalizeFirstLetter(field);
  }
}
